import React, { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { getStyle, installBuildingExtrusion, applySatelliteZoomCap } from "../../lib/mapStyles";
import { getCountryBoundary, installCountryMask } from "../../lib/countryMask";
import { useMapFullscreen, MapFullscreenButton } from "../MapFullscreenToggle";
import { MALI_CENTRE, MALI_INIT_ZOOM, hasWebGL } from "../heatmap/heatmapHelpers";
import { checkPlanificationAccess } from "../../lib/api";
import { useT } from "../../lib/lang";
import { PlanificationToolbar } from "./PlanificationToolbar";
import { PlanificationSidePanel } from "./PlanificationSidePanel";
import { MapStyleSwitcher } from "../map/MapStyleSwitcher";
import { PlanificationMeasureReadout } from "./PlanificationMeasureReadout";
import { PlanificationDrawActions } from "./PlanificationDrawActions";
import { PlanificationToolSwitchConfirm } from "./PlanificationToolSwitchConfirm";
import { PlanificationAnnotationForm } from "./PlanificationAnnotationForm";
import { PlanificationConfirmDialog } from "./PlanificationConfirmDialog";
import { PlanificationHistoryActions } from "./PlanificationHistoryActions";
import {
  createPlanificationDrawController,
  TOOL_TO_MODE,
  contractForMode,
  modeNameForContract,
  sessionFeatureToTerraDrawFeature,
  getCurrentlyDrawingVertexCount,
  getCurrentlyDrawingFeature,
} from "../../lib/planificationDraw";
import { usePlanificationSession } from "../../lib/planificationSession";
import { ACTION_TYPES } from "../../lib/planificationHistory";
import { distanceMeters, previewLineLengthMeters, previewPolygonAreaSqMeters, formatDistance, formatArea, OBJECT_TYPES, PURPOSES } from "../../lib/planificationGeo";
import { StrategyAuthGate } from "../strategy/StrategyAuthGate";
import { StrategyProjectsPanel } from "../strategy/StrategyProjectsPanel";
import { StrategyAdministrationPanel } from "../strategy/StrategyAdministrationPanel";

/** Drawing/measure tool keys that put the map into an ACTUAL Terra Draw
 *  drawing mode (as opposed to "pan", "zoom_in"/"zoom_out" which are
 *  either idle or one-shot actions). */
const DRAW_TOOL_KEYS = new Set(["draw_polygon", "draw_line", "measure_distance", "measure_area"]);
const POLYGON_TOOL_KEYS = new Set(["draw_polygon", "measure_area"]);
const MIN_VERTICES_TO_FINISH = { draw_polygon: 3, measure_area: 3, draw_line: 2, measure_distance: 2 };

const REASON_MESSAGE_KEYS = {
  NOT_POLYGON_GEOJSON: "planificationErrorNotPolygonGeojson",
  INVALID_COORDINATES: "planificationErrorInvalidCoordinates",
  RING_NOT_CLOSED: "planificationErrorRingNotClosed",
  TOO_FEW_VERTICES: "planificationErrorTooFewVertices",
  DUPLICATE_CONSECUTIVE_VERTICES: "planificationErrorDuplicateConsecutiveVertices",
  DEGENERATE_RING: "planificationErrorDegenerateRing",
  SELF_INTERSECTING: "planificationErrorSelfIntersecting",
  TANGENT_CONTACT: "planificationErrorTangentContact",
  NOT_LINESTRING_GEOJSON: "planificationErrorNotLinestringGeojson",
  TOO_FEW_POSITIONS: "planificationErrorTooFewPositions",
};

/**
 * Admin-only "Planification" workspace.
 * Phase 1 (Iter93): tab shell, independent map, side panel placeholders.
 * Phase 2A (Iter95): Terra Draw engine wired in idle, Turf/GeoJSON foundations.
 * Phase 2B (Iter96): the 4 real tools — polygon, line, measure distance,
 * measure area — backed by the Phase 2A foundations, strict validation,
 * and a session FeatureCollection that survives every basemap style
 * switch (Terra Draw itself is destroyed/recreated on every switch,
 * since `map.setStyle()` wipes its layers — the FeatureCollection in
 * `usePlanificationSession` never is; see the style-switch effect below).
 *
 * Deliberately independent of HeatmapCommandCenter/CommandCenterTab: own
 * MapLibre instance, own state, NO alert data, NO WebSocket subscription
 * of any kind, NO persistence beyond this session (no localStorage, no
 * server call) — see routers/planification.py for why no Mongo
 * collection exists for this yet either (reserved for Phase 2C/3).
 */
export const PlanificationTab = ({ onUnsavedChange } = {}) => {
  const [t] = useT();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const didInitStyleRef = useRef(false);
  // Re-created on every basemap style switch because MapLibre's
  // setStyle() wipes all custom sources/layers, including Terra Draw's
  // own internal render layers.
  const drawControllerRef = useRef(null);
  // Cancel-token for the above: a rapid style flip queues a 2nd
  // `styledata` listener before the 1st has fired. Each switch stamps
  // its own generation here; a `styledata` callback only (re)creates the
  // draw controller if it's still the MOST RECENT switch by the time it
  // fires — otherwise it's stale and must no-op.
  const styleGenRef = useRef(0);
  const [webglOK] = useState(hasWebGL);
  const [styleKind, setStyleKind] = useState("hybrid"); // default: Satellite
  const [activeTool, setActiveTool] = useState("pan");
  const activeToolRef = useRef(activeTool);
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  // Phase 1 requirement: collapsed on mobile, expanded on desktop.
  const [panelOpen, setPanelOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [access, setAccess] = useState("checking"); // checking | allowed | denied
  // Phase 3B — "Carte" (unchanged Phase 2A/B/C session-only drawing,
  // still gated by the LEGACY shared-admin `access` check above) vs
  // "Projets"/"Administration" (persisted, gated by the NEW individual
  // `admin_account` system via <StrategyAuthGate> — never the legacy
  // session). Switching views never touches or resets the live
  // drawing session underneath.
  const [workspaceView, setWorkspaceView] = useState("carte");
  const [activeStrategyProject, setActiveStrategyProject] = useState(null);
  const { isFs, toggle: toggleFs, fsWrapperClass } = useMapFullscreen(mapRef);

  // Phase 2B — session FeatureCollection (source of truth, survives
  // style switches) + live-draw UI state (vertex count, measurement
  // readout, pending tool-switch confirmation).
  const session = usePlanificationSession();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);
  const [vertexCount, setVertexCount] = useState(0);
  const [liveStats, setLiveStats] = useState(null); // {kind, segmentM, totalM, areaSqM} | null
  const [pendingTool, setPendingTool] = useState(null);
  // Phase 2C (spec §4/§5/§6/§8) — selection, annotation draft, and
  // confirm-dialog UI state. None of this lives in `usePlanificationSession`:
  // selection is ephemeral/not historised, and a pending annotation draft
  // is BY DEFINITION not in the session yet (spec §4.1).
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [pendingAnnotation, setPendingAnnotation] = useState(null); // {id, geometry} | null
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  // Guards the bulk "Effacer tous les dessins" path: Terra Draw's own
  // per-feature "delete" change event (already wired to
  // `handleFeatureDeleted` -> `session.removeFeature`, one history entry
  // EACH) must be suppressed while a bulk clear is in flight, because
  // `clearAllFeatures()` below records that whole operation as ONE
  // logical action instead (spec §6.2/§8.5).
  const isClearingAllRef = useRef(false);

  // ---------- Server-side access check ----------
  useEffect(() => {
    let cancelled = false;
    checkPlanificationAccess()
      .then(() => { if (!cancelled) setAccess("allowed"); })
      .catch(() => { if (!cancelled) setAccess("denied"); });
    return () => { cancelled = true; };
  }, []);

  // ---------- Terra Draw event handlers (stable across re-renders; the
  // controller factory captures these once per map/style-switch, but
  // they always read the LATEST session/activeTool via refs). ----------
  const handleInvalidFinish = useCallback((reasonCode, objectType) => {
    const titleKey = objectType === "polygon" ? "planificationInvalidPolygon" : "planificationInvalidGeometry";
    const messageKey = REASON_MESSAGE_KEYS[reasonCode];
    toast.error(t[titleKey], { description: messageKey ? t[messageKey] : reasonCode, duration: 4000 });
  }, [t]);

  const handleFeatureFinalized = useCallback((id, modeName, geometry, isNew) => {
    const contract = contractForMode(modeName);
    if (!contract) return;
    if (isNew) {
      const isMeasurement = contract.purpose === "measurement";
      sessionRef.current.createFeature({
        id,
        objectType: contract.objectType,
        purpose: contract.purpose,
        geometry,
        color: isMeasurement ? "#f59e0b" : "#00B894",
        opacity: isMeasurement ? 0.25 : 0.4,
      });
    } else {
      sessionRef.current.updateFeatureGeometry(id, geometry);
    }
    setVertexCount(0);
    setLiveStats(null);
  }, []);

  const handleFeatureDeleted = useCallback((id) => {
    if (isClearingAllRef.current) return; // bulk clear is historised as ONE action by handleConfirmClearAll
    sessionRef.current.removeFeature(id);
    setSelectedFeatureId((cur) => (cur === id ? null : cur));
  }, []);

  // Phase 2C §5.1 — Terra Draw's own `select`/`deselect` events, synced
  // straight to React state so the side panel always matches the map
  // (spec: "Synchroniser la sélection avec le panneau latéral").
  const handleMapSelect = useCallback((id) => setSelectedFeatureId(id), []);
  const handleMapDeselect = useCallback((id) => setSelectedFeatureId((cur) => (cur === id ? null : cur)), []);

  // Phase 2C §4.1 — a Point feature finalizes on a single click; defer
  // ever touching the session (and therefore history) until the title
  // form is actually saved. Lock the toolbar into "pan" immediately so
  // a second stray click can't stamp a 2nd orphan point while the form
  // is open.
  const handleAnnotationDrafted = useCallback((id, geometry) => {
    switchToTool("pan");
    setPendingAnnotation({ id, geometry });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAnnotation = useCallback(({ title, description, color }) => {
    if (!pendingAnnotation) return;
    sessionRef.current.createFeature({
      id: pendingAnnotation.id,
      objectType: OBJECT_TYPES.ANNOTATION,
      purpose: PURPOSES.DRAWING,
      geometry: pendingAnnotation.geometry,
      name: title,
      description,
      color,
      opacity: 1,
    });
    drawControllerRef.current?.draw.updateFeatureProperties(pendingAnnotation.id, { color, opacity: 1 });
    setPendingAnnotation(null);
  }, [pendingAnnotation]);

  const handleCancelAnnotation = useCallback(() => {
    const draw = drawControllerRef.current?.draw;
    if (pendingAnnotation && draw?.hasFeature(pendingAnnotation.id)) draw.removeFeatures([pendingAnnotation.id]);
    setPendingAnnotation(null);
  }, [pendingAnnotation]);

  // Phase 2C §5.3/§7.2 — rename/describe/recolor/re-opacify WHATEVER is
  // currently selected (any object_type). Historised by
  // `session.updateFeature` (UPDATE action) exactly like a geometry
  // edit; mirrored into Terra Draw's own store so the live render
  // updates immediately (function-based styles in planificationDraw.js
  // read `properties.color`/`properties.opacity` per-feature).
  const handleSaveProperties = useCallback((values) => {
    if (!selectedFeatureId) return;
    sessionRef.current.updateFeature(selectedFeatureId, values);
    drawControllerRef.current?.draw.updateFeatureProperties(selectedFeatureId, { color: values.color, opacity: values.opacity });
    toast.message(t.planificationSave);
  }, [selectedFeatureId, t]);

  const handleCancelEdit = useCallback(() => setSelectedFeatureId(null), []);

  const handleRequestDelete = useCallback(() => { if (selectedFeatureId) setDeleteConfirmId(selectedFeatureId); }, [selectedFeatureId]);

  const handleConfirmDelete = useCallback(() => {
    const draw = drawControllerRef.current?.draw;
    if (deleteConfirmId) {
      if (draw?.hasFeature(deleteConfirmId)) draw.removeFeatures([deleteConfirmId]); // -> onChange("delete") -> handleFeatureDeleted
      else sessionRef.current.removeFeature(deleteConfirmId); // defensive fallback, should not normally be reached
      toast.message(t.planificationObjectDeleted);
    }
    setDeleteConfirmId(null);
  }, [deleteConfirmId, t]);

  const handleCancelDeleteConfirm = useCallback(() => setDeleteConfirmId(null), []);

  // Phase 2C §8.5 — "Effacer tous les dessins" as ONE undo-able action.
  const handleConfirmClearAll = useCallback(() => {
    const draw = drawControllerRef.current?.draw;
    const ids = sessionRef.current.featureCollection.features.map((f) => f.id);
    if (draw && ids.length) {
      isClearingAllRef.current = true;
      draw.removeFeatures(ids.filter((id) => draw.hasFeature(id)));
      isClearingAllRef.current = false;
    }
    sessionRef.current.clearAllFeatures();
    setSelectedFeatureId(null);
    setClearAllConfirmOpen(false);
    toast.message(t.planificationAllDrawingsCleared);
  }, [t]);

  // Phase 2C §7.2 — "Centrer sur la carte".
  const handleCenterOnMap = useCallback(() => {
    const map = mapRef.current;
    const feature = sessionRef.current.featureCollection.features.find((f) => f.id === selectedFeatureId);
    if (!map || !feature) return;
    if (feature.geometry.type === "Point") {
      map.flyTo({ center: feature.geometry.coordinates, zoom: Math.max(map.getZoom(), 12), duration: 600 });
      return;
    }
    const ring = feature.geometry.type === "Polygon" ? feature.geometry.coordinates[0] : feature.geometry.coordinates;
    const lngs = ring.map((c) => c[0]);
    const lats = ring.map((c) => c[1]);
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 70, maxZoom: 15, duration: 600 });
  }, [selectedFeatureId]);

  // Phase 2C §7.2 — select from the side panel list: switches to
  // pan/select AND tells Terra Draw itself to highlight that feature,
  // so the map and the panel never disagree about what's selected.
  const handleSelectFromList = useCallback((id) => {
    switchToTool("pan");
    drawControllerRef.current?.draw.selectFeature(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 2C §6.6 — after an Undo/Redo, mirror the change into the LIVE
  // Terra Draw store (session.undo()/redo() only touch React state; they
  // know nothing about MapLibre/Terra Draw by design, see
  // planificationSession.js). `applied` tells us which snapshot
  // ("before" for undo, "after" for redo) is now the current truth.
  const syncTerraDrawForHistoryAction = useCallback((result) => {
    if (!result) return;
    const { action, applied } = result;
    const draw = drawControllerRef.current?.draw;
    if (!draw) return;

    if (action.type === ACTION_TYPES.CLEAR_ALL) {
      isClearingAllRef.current = true;
      if (applied === "before") {
        const toAdd = action.before
          .filter((f) => !draw.hasFeature(f.id))
          .map((f) => { const modeName = modeNameForContract(f.properties.object_type, f.properties.purpose); return modeName ? sessionFeatureToTerraDrawFeature(f, modeName) : null; })
          .filter(Boolean);
        if (toAdd.length) draw.addFeatures(toAdd);
      } else {
        const idsToRemove = action.before.map((f) => f.id).filter((id) => draw.hasFeature(id));
        if (idsToRemove.length) draw.removeFeatures(idsToRemove);
      }
      isClearingAllRef.current = false;
      setSelectedFeatureId(null);
      return;
    }

    const snapshot = applied === "before" ? action.before : action.after;
    if (action.type === ACTION_TYPES.CREATE || action.type === ACTION_TYPES.DELETE) {
      if (snapshot && !draw.hasFeature(action.featureId)) {
        const modeName = modeNameForContract(snapshot.properties.object_type, snapshot.properties.purpose);
        if (modeName) draw.addFeatures([sessionFeatureToTerraDrawFeature(snapshot, modeName)]);
      } else if (!snapshot && draw.hasFeature(action.featureId)) {
        draw.removeFeatures([action.featureId]);
        setSelectedFeatureId((cur) => (cur === action.featureId ? null : cur));
      }
    } else if (action.type === ACTION_TYPES.UPDATE && snapshot && draw.hasFeature(action.featureId)) {
      draw.updateFeatureGeometry(action.featureId, snapshot.geometry);
      draw.updateFeatureProperties(action.featureId, { color: snapshot.properties.color, opacity: snapshot.properties.opacity });
    }
  }, []);

  const handleUndo = useCallback(() => { syncTerraDrawForHistoryAction(sessionRef.current.undo()); }, [syncTerraDrawForHistoryAction]);
  const handleRedo = useCallback(() => { syncTerraDrawForHistoryAction(sessionRef.current.redo()); }, [syncTerraDrawForHistoryAction]);

  const handleDrawChange = useCallback(() => {
    const draw = drawControllerRef.current?.draw;
    if (!draw) return;
    const modeName = TOOL_TO_MODE[activeToolRef.current];
    if (!modeName || !DRAW_TOOL_KEYS.has(activeToolRef.current)) return;
    const count = getCurrentlyDrawingVertexCount(draw, modeName);
    setVertexCount(count);
    const feature = getCurrentlyDrawingFeature(draw, modeName);
    if (!feature) { setLiveStats(null); return; }
    if (POLYGON_TOOL_KEYS.has(activeToolRef.current)) {
      const ring = feature.geometry?.coordinates?.[0] || [];
      setLiveStats({ kind: "polygon", areaSqM: previewPolygonAreaSqMeters(ring) });
    } else {
      const coords = feature.geometry?.coordinates || [];
      const valid = coords.filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
      const segmentM = valid.length >= 2 ? distanceMeters(valid[valid.length - 2], valid[valid.length - 1]) : 0;
      setLiveStats({ kind: "line", segmentM, totalM: previewLineLengthMeters(valid) });
    }
  }, []);

  /** Builds the Terra-Draw-ready rehydration list from the CURRENT
   *  session FeatureCollection — called right before (re)creating a
   *  controller so every style switch re-injects whatever is already
   *  finalized, with the same ids/geometry/properties (spec §9). */
  const buildInitialFeatures = useCallback(
    () => sessionRef.current.featureCollection.features
      .map((feature) => {
        const modeName = modeNameForContract(feature.properties.object_type, feature.properties.purpose);
        return modeName ? { feature, modeName } : null;
      })
      .filter(Boolean),
    []
  );

  const createController = useCallback((map) => {
    const controller = createPlanificationDrawController(map, {
      initialFeatures: buildInitialFeatures(),
      onInvalidFinish: handleInvalidFinish,
      onFeatureFinalized: handleFeatureFinalized,
      onFeatureDeleted: handleFeatureDeleted,
      onAnnotationDrafted: handleAnnotationDrafted,
      onSelect: handleMapSelect,
      onDeselect: handleMapDeselect,
      onChange: handleDrawChange,
    });
    // Dev/test-only hook (never shipped in a production build — guarded
    // by NODE_ENV, same convention CRA uses for its own dev warnings).
    // Terra Draw's vertex/marker drag handles render on a WebGL canvas
    // with no per-handle DOM node, so a real Playwright test cannot
    // target them by selector (documented tooling limitation since
    // Phase 2B QA). This hook lets an automated test trigger the exact
    // SAME application code path a real drag-and-release commits
    // (`draw.updateFeatureGeometry` — the same Terra Draw store mutation
    // a finished SelectMode drag performs — immediately followed by the
    // same `handleFeatureFinalized(id, mode, geometry, isNew=false)`
    // call Terra Draw's own `finish` event would trigger) — so Undo/Redo,
    // history recording, geometry (re-)validation, and UUID preservation
    // are exercised end-to-end for real, without re-implementing a
    // second/divergent update path. It does NOT prove Terra Draw's own
    // WebGL hit-testing/handle rendering works for a real mouse pointer —
    // that remains out of scope here (third-party library concern,
    // previously confirmed once via a direct manual Playwright pixel
    // drag in the Phase 2B QA pass).
    if (process.env.NODE_ENV !== "production") {
      window.__plan_draw = controller.draw;
      window.__plan_map = map;
      window.__plan_simulate_edit = (featureId, newGeometry) => {
        const snap = controller.draw.getSnapshotFeature(featureId);
        if (!snap) return false;
        controller.draw.updateFeatureGeometry(featureId, newGeometry);
        handleFeatureFinalized(featureId, snap.properties.mode, newGeometry, false);
        return true;
      };
    }
    return controller;
  }, [buildInitialFeatures, handleInvalidFinish, handleFeatureFinalized, handleFeatureDeleted, handleAnnotationDrafted, handleMapSelect, handleMapDeselect, handleDrawChange]);

  // ---------- Map init ----------
  useEffect(() => {
    if (access !== "allowed" || !webglOK || !containerRef.current || mapRef.current) return undefined;
    didInitStyleRef.current = false;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getStyle(styleKind),
      center: [MALI_CENTRE.lng, MALI_CENTRE.lat],
      zoom: MALI_INIT_ZOOM,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    });
    applySatelliteZoomCap(map, styleKind);
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-left");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;

    const onLoad = () => {
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, styleKind);
      });
      drawControllerRef.current = createController(map);
      drawControllerRef.current.setActiveTool(activeToolRef.current);
    };
    if (map.isStyleLoaded()) onLoad();
    else map.once("load", onLoad);

    const ro = new ResizeObserver(() => { try { map.resize(); } catch (_e) { /* noop */ } });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      drawControllerRef.current?.destroy();
      drawControllerRef.current = null;
      map.remove();
      mapRef.current = null;
      didInitStyleRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, webglOK]);

  // ---------- Re-style switch (Plan/Sombre/Satellite) ----------
  // Preserves every finalized object (spec §9 — the approved Phase 2A
  // follow-up requirement): the session FeatureCollection (held by
  // `usePlanificationSession`, entirely OUTSIDE this MapLibre/Terra Draw
  // instance) is untouched by `setStyle()`; only Terra Draw's own layers
  // are destroyed and recreated, then immediately rehydrated from that
  // same session via `buildInitialFeatures()` — same ids, geometry and
  // properties, no new "create" events synthesized during rehydration
  // (addFeatures() is a silent bulk load, it does not fire `finish`).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!didInitStyleRef.current) { didInitStyleRef.current = true; return; }
    if (vertexCount > 0) {
      // An unfinished drawing is in progress — never lose it silently
      // (spec §9 "dessin en cours"). Cancel it explicitly first (clean,
      // documented behaviour) rather than letting setStyle() wipe it
      // with no trace; the (minor) alternative of attempting to carry a
      // PROVISIONAL geometry across a full style/source teardown is not
      // technically reliable, so we are explicit about the trade-off
      // here instead of silently losing vertices.
      drawControllerRef.current?.cancelCurrentDrawing();
      toast.message(t.planificationCancelDrawing, { description: t.planificationSwitchToolConfirmMessage, duration: 3000 });
      setVertexCount(0);
      setLiveStats(null);
    }
    drawControllerRef.current?.destroy();
    drawControllerRef.current = null;
    const myGen = ++styleGenRef.current;
    map.setStyle(getStyle(styleKind));
    applySatelliteZoomCap(map, styleKind);
    map.once("styledata", () => {
      if (!mapRef.current) return; // unmounted/torn down while the style was loading
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, styleKind);
      });
      if (myGen !== styleGenRef.current) return; // a newer switch started — stale callback, no-op
      drawControllerRef.current = createController(map);
      drawControllerRef.current.setActiveTool(activeToolRef.current);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleKind]);

  const handleToolAction = useCallback((action) => {
    const map = mapRef.current;
    if (!map) return;
    if (action === "zoomIn") map.zoomIn();
    else if (action === "zoomOut") map.zoomOut();
  }, []);

  const switchToTool = useCallback((toolKey) => {
    setActiveTool(toolKey);
    drawControllerRef.current?.setActiveTool(toolKey);
    setVertexCount(0);
    setLiveStats(null);
  }, []);

  // Single-active-mode constraint + "changement de mode" confirm (spec §8).
  // While an annotation draft is awaiting its title form, block every
  // other tool switch outright (spec §4.1's form is effectively modal —
  // switching tools mid-form would orphan the just-placed point).
  const requestSelectTool = useCallback((toolKey) => {
    if (pendingAnnotation || toolKey === activeTool) return;
    if (DRAW_TOOL_KEYS.has(activeTool) && vertexCount > 0) {
      setPendingTool(toolKey);
      return;
    }
    switchToTool(toolKey);
  }, [activeTool, vertexCount, switchToTool, pendingAnnotation]);

  const confirmPendingSwitch = useCallback(() => {
    drawControllerRef.current?.cancelCurrentDrawing(); // provisional only — never historised (spec §12)
    switchToTool(pendingTool);
    setPendingTool(null);
  }, [pendingTool, switchToTool]);

  const cancelPendingSwitch = useCallback(() => setPendingTool(null), []);

  const handleFinishDrawing = useCallback(() => drawControllerRef.current?.finishCurrentDrawing(), []);
  const handleCancelDrawing = useCallback(() => {
    drawControllerRef.current?.cancelCurrentDrawing();
    setVertexCount(0);
    setLiveStats(null);
  }, []);

  // Phase 2C §8.1/§8.3 — "unsaved work" = any finalized object exists
  // (Planification has no durable save yet, so the moment ANY object
  // exists it is, by definition, unsaved). Deliberately NOT derived from
  // zoom/pan/style/fullscreen/panel state — none of those ever touch
  // `featureCollection` (spec: "il ne doit pas être activé par un simple
  // zoom, Pan ou changement de fond cartographique").
  const hasUnsavedChanges = session.featureCollection.features.length > 0;
  useEffect(() => { onUnsavedChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onUnsavedChange]);

  // Phase 2C §8.3 — native browser dialog only; no custom text is
  // possible in any modern browser, and no mobile browser is guaranteed
  // to honour it at all. Never a substitute for the internal nav guard
  // above, which covers the one case this CAN'T: switching admin tabs
  // inside the SPA.
  useEffect(() => {
    const handler = (e) => { if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  if (access === "denied") {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0b0f14] text-zinc-400 p-8" data-testid="planification-access-denied">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm">
          <ShieldAlert className="w-8 h-8 text-amber-400" />
          <p className="text-sm">{t.planificationAccessDenied}</p>
        </div>
      </div>
    );
  }

  const canFinish = vertexCount >= (MIN_VERTICES_TO_FINISH[activeTool] || Infinity);
  const drawActionsKind = DRAW_TOOL_KEYS.has(activeTool) ? (POLYGON_TOOL_KEYS.has(activeTool) ? "polygon" : "line") : null;
  const selectedFeature = selectedFeatureId ? session.featureCollection.features.find((f) => f.id === selectedFeatureId) || null : null;

  return (
    <div className="flex-1 flex flex-col bg-[#0b0f14] text-zinc-200" data-testid="planification-tab">
      {/* Phase 3B sub-tabs — "Carte" is the untouched Phase 2A/B/C
          session-only drawing workspace; "Projets"/"Administration"
          are new, persisted, gated by the individual admin_account
          system (<StrategyAuthGate>), never by the legacy session. */}
      <div className="flex items-center gap-1 px-4 pt-2 border-b border-zinc-800" data-testid="strategy-workspace-tabs">
        {[
          { key: "carte", label: "Carte" },
          { key: "projets", label: "Projets" },
          { key: "administration", label: "Administration" },
        ].map((tab) => (
          <button
            key={tab.key}
            data-testid={`strategy-workspace-tab-${tab.key}`}
            onClick={() => setWorkspaceView(tab.key)}
            className={`text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-2 border-b-2 -mb-px ${
              workspaceView === tab.key ? "border-rescue-red text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {workspaceView === "projets" && (
        <StrategyAuthGate>
          <div className="p-4 max-w-xl">
            <StrategyProjectsPanel session={session} activeProject={activeStrategyProject} setActiveProject={setActiveStrategyProject} />
          </div>
        </StrategyAuthGate>
      )}
      {workspaceView === "administration" && (
        <StrategyAuthGate>
          <StrategyAdministrationPanel />
        </StrategyAuthGate>
      )}
      {/* Phase 3B fix: the Carte block below stays ALWAYS mounted (CSS
          `hidden` instead of unmounting) — MapLibre/Terra Draw attach
          directly to the `containerRef` DOM node on mount and have no
          re-attach path; unmounting/remounting that node when
          switching tabs would silently break the map. */}
      <div className={workspaceView === "carte" ? "contents" : "hidden"}>
      {/* Compact header — style switcher + Phase 2C history actions;
          this workspace has no alert/time-range/refresh controls, it is
          not the Carte tab. */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-zinc-800 overflow-x-auto cmd-scroll-row">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 shrink-0">
          {t.planification}
          {activeStrategyProject && <span className="text-zinc-600 normal-case tracking-normal"> — projet ouvert</span>}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <PlanificationHistoryActions
            canUndo={session.canUndo}
            canRedo={session.canRedo}
            hasObjects={hasUnsavedChanges}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onClearAll={() => setClearAllConfirmOpen(true)}
          />
          <MapStyleSwitcher
            styleKind={styleKind}
            onChange={setStyleKind}
            align="right"
            toggleTestId="planification-style-toggle"
            backdropTestId="planification-style-menu-backdrop"
            menuTestId="planification-style-menu"
            optionTestId={(key) => `planification-style-${key}`}
          />
        </div>
      </div>

      {/* MAP + SIDE PANEL */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div
          className={fsWrapperClass("relative w-full h-[60vh] lg:h-auto lg:flex-1 lg:min-h-0")}
          data-testid="planification-map-container"
        >
          <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} />
          {access === "checking" && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center bg-[#0b0f14]/80 text-xs text-zinc-500 font-mono uppercase tracking-widest">
              …
            </div>
          )}
          <PlanificationToolbar activeTool={activeTool} onSelectTool={requestSelectTool} onAction={handleToolAction} />
          <MapFullscreenButton isFs={isFs} onToggle={toggleFs} className="absolute top-3 right-3 z-[450]" />

          {liveStats && (
            <PlanificationMeasureReadout
              kind={liveStats.kind}
              segmentLabel={formatDistance(liveStats.segmentM || 0)}
              totalLabel={formatDistance(liveStats.totalM || 0)}
              areaLabel={formatArea(liveStats.areaSqM || 0)}
            />
          )}
          {drawActionsKind && (
            <PlanificationDrawActions kind={drawActionsKind} canFinish={canFinish} onFinish={handleFinishDrawing} onCancel={handleCancelDrawing} />
          )}
          {pendingTool && <PlanificationToolSwitchConfirm onConfirm={confirmPendingSwitch} onCancel={cancelPendingSwitch} />}
          {pendingAnnotation && (
            <PlanificationAnnotationForm
              coordinates={pendingAnnotation.geometry.coordinates}
              onSave={handleSaveAnnotation}
              onCancel={handleCancelAnnotation}
            />
          )}
          {deleteConfirmId && (
            <PlanificationConfirmDialog
              testId="planification-delete-confirm"
              title={t.planificationDeleteConfirmTitle}
              message={t.planificationDeleteConfirmMessage}
              confirmLabel={t.planificationDeleteAction}
              cancelLabel={t.planificationCancelAction}
              danger
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDeleteConfirm}
            />
          )}
          {clearAllConfirmOpen && (
            <PlanificationConfirmDialog
              testId="planification-clear-all-confirm"
              title={t.planificationClearAllConfirmTitle}
              message={t.planificationClearAllConfirmMessage}
              confirmLabel={t.planificationClearAllDrawings}
              cancelLabel={t.planificationCancelAction}
              danger
              onConfirm={handleConfirmClearAll}
              onCancel={() => setClearAllConfirmOpen(false)}
            />
          )}
        </div>
        <PlanificationSidePanel
          open={panelOpen}
          onToggleOpen={() => setPanelOpen((v) => !v)}
          features={session.featureCollection.features}
          selectedFeature={selectedFeature}
          onSelectFeature={handleSelectFromList}
          onSaveProperties={handleSaveProperties}
          onCancelEdit={handleCancelEdit}
          onDeleteSelected={handleRequestDelete}
          onCenterOnMap={handleCenterOnMap}
        />
      </div>
      </div>
    </div>
  );
};
